import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { LockedFunds, Wallet, walletRepository } from "../../repositories/WalletRepository";
import { Transaction } from "../../models/Transactions";

export interface WalletInfos {
    balance: number | null;
    lnurl: string;
    transactions: Transaction[];
    isLoading: boolean;
    error: string | null;
    hasMore: boolean;
    currentPage: number;
    lockedFunds?: LockedFunds;
    nwcConnectionString?: string;
}

const initialState: WalletInfos = {
    balance: null,
    lnurl: '',
    transactions: [],
    isLoading: false,
    error: null,
    hasMore: true,
    currentPage: 1,
    nwcConnectionString: undefined
}

export const fetchTransactions = createAsyncThunk(
    'wallet/fetchTransactions',
    async ({ page, perPage }: { page: number, perPage: number }, { rejectWithValue }) => {
        try {
            const transactions = await walletRepository.getTransactions(perPage, page);

            return { 
                transactions,
                hasMore: transactions.length === perPage
            };
        } catch (error) {
            return rejectWithValue('Error fetching transactions');
        }
    }
);

export const refreshLastTransactions = createAsyncThunk(
    'wallet/refreshLastTransactions',
    async () => {
        try {
            const transactions = await walletRepository.getTransactions(20, 1);

            return { 
                transactions
            };
        } catch (error) {
            console.error('Error fetching transactions');
            return {
                transactions: []
            };
        }
    }
);

const walletSlice = createSlice({
    name: 'wallet',
    initialState,
    reducers: {
        setInfos: (state, action: PayloadAction<Wallet>) => {
            state.balance = action.payload.balance;
            state.lnurl = action.payload.lnurl;
            if(action.payload.lockedFunds)
                state.lockedFunds = action.payload.lockedFunds;
            if(action.payload.nwcConnectionString)
                state.nwcConnectionString = action.payload.nwcConnectionString;
        },
        setBalance: (state, action: PayloadAction<number>) => {
            state.balance = action.payload;
        },
        setNWCConnectionString: (state, action: PayloadAction<string>) => {
            state.nwcConnectionString = action.payload;
        },
        resetTransactions: (state) => {
            state.balance = null;
            state.transactions = [];
            state.currentPage = 1;
            state.hasMore = true;
        },
        addTransaction: (state, action: PayloadAction<Transaction>) => {
            state.transactions = [
                ...state.transactions.filter(t => t.paymentHash !== action.payload.paymentHash),
                action.payload
            ].sort((a, b) => {
                const dateA = new Date(a.time).getTime();
                const dateB = new Date(b.time).getTime();
                return dateB - dateA;
            });
        },
        markTransactionAsRead: (state, action: PayloadAction<string>) => {
            const transaction = state.transactions.find(t => t.paymentHash === action.payload);
            if (transaction) {
                transaction.read = true;
            }
        },
        markTransactionAsAnnounced: (state, action: PayloadAction<string>) => {
            const transaction = state.transactions.find(t => t.paymentHash === action.payload);
            if (transaction) {
                transaction.announced = true;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTransactions.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchTransactions.fulfilled, (state, action) => {
                state.isLoading = false;
                
                // Create a map of existing transactions by paymentHash
                const existingTransactions = new Map(
                    state.transactions.map(t => [t.paymentHash, t])
                );

                // Add new unique transactions
                action.payload.transactions.forEach(newTransaction => {
                    if (!existingTransactions.has(newTransaction.paymentHash)) {
                        existingTransactions.set(newTransaction.paymentHash, newTransaction);
                    }
                });

                // Convert map back to array and sort by time
                state.transactions = Array.from(existingTransactions.values())
                    .sort((a, b) => {
                        const dateA = new Date(a.time).getTime();
                        const dateB = new Date(b.time).getTime();
                        return dateB - dateA; // Newest first
                    });

                state.hasMore = action.payload.hasMore;
                state.currentPage += 1;
            })
            .addCase(fetchTransactions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(refreshLastTransactions.fulfilled, (state, action) => {                
                // Create a map of existing transactions by paymentHash
                const existingTransactions = new Map(
                    state.transactions.map(t => [t.paymentHash, t])
                );

                // Add new unique transactions
                action.payload.transactions.forEach(newTransaction => {
                    if (!existingTransactions.has(newTransaction.paymentHash)) {
                        existingTransactions.set(newTransaction.paymentHash, newTransaction);
                    }
                });

                // Convert map back to array and sort by time
                state.transactions = Array.from(existingTransactions.values())
                    .sort((a, b) => {
                        const dateA = new Date(a.time).getTime();
                        const dateB = new Date(b.time).getTime();
                        return dateB - dateA; // Newest first
                    });
            });
    }
});

export const { setInfos, setBalance, setNWCConnectionString, resetTransactions, addTransaction, markTransactionAsRead, markTransactionAsAnnounced } = walletSlice.actions;
export default walletSlice.reducer; 